import React from 'react';
import styled from 'styled-components';

import { Command, tabActions } from '@store/slices';
import { BranchNodeState, NodeField } from '@components/Flow/Nodes';
import { useNodeData } from '@common/hooks/useNodeData';
import { useDispatch, useSelector } from 'react-redux';
import { Option } from '@common/BucketInput/BucketField';
import { Expression } from '@common/Expression';
import { Select } from '@common/Select';
import { Label } from '@common/Label';
import { State } from '@store/index';
import { useParams } from 'react-router-dom';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 8px;
`;

interface Props {
  command: Command;
  field: NodeField;
}

export const SingleSwitch: React.FC<Props> = ({ command, field }) => {
  const state: BranchNodeState = command.state;

  const allNodeErrors = useSelector((state: State) => state.tabs.active.nodeErrors);

  const { workflow_id } = useParams();

  const { getOptions, dataEdges } = useNodeData(command.node.id);
  const { input, operator, value } = state.condition;

  const dispatch = useDispatch();
  const options = getOptions(command.node.id);

  const searchTypeOptions: Option[] = [
    { label: 'Equals', value: 'equals' },
    { label: 'Is Undefined', value: 'is_undefined' },
    { label: 'Is Defined', value: 'is_defined' },
    { label: 'Starts with', value: 'starts_with' },
    { label: 'Ends with', value: 'ends_with' },
    { label: 'Contains', value: 'contains' },
    { label: 'Greater Than', value: 'greater_than' },
    { label: 'Greater Than or Equal', value: 'greater_than_equal' },
    { label: 'Less Than', value: 'less_than' },
    { label: 'Less Than or Equal', value: 'less_than_equal' },
    { label: 'Does not equal', value: 'does_not_equal' },
    { label: 'Does not start with', value: 'does_not_start_with' },
    { label: 'Does not end with', value: 'does_not_end_with' },
    { label: 'Does not contain', value: 'does_not_contain' },
  ];

  const searchOptionsWithoutValue = ['is_undefined', 'is_defined'];

  const updateField = (value: string, fieldName: string) => {
    dispatch(
      tabActions.nodeInputUpdate({
        nodeId: command.node.id,
        fieldId: field.id,
        value: value,
        fieldName,
      })
    );

    dispatch(tabActions.setSaved(false));
  };

  const nodeErrors = allNodeErrors.filter((nodeError) => nodeError.id === command.node.id);

  // condition input field error
  const inputError = nodeErrors?.find(
    (nodeError) =>
      nodeError.id === command.node.id &&
      nodeError?.workflowId === workflow_id &&
      nodeError?.fields?.find((nodeField) => nodeField.id === 'input')
  );
  const inputErrorField = inputError ? inputError?.fields?.find((nodeField) => nodeField.id === 'input') : null;
  const inputErrorMessage = inputErrorField ? inputErrorField.message : undefined;

  // condition value field error
  const valueError = nodeErrors?.find(
    (nodeError) =>
      nodeError.id === command.node.id &&
      nodeError?.workflowId === workflow_id &&
      nodeError?.fields?.find((nodeField) => nodeField.id === 'value')
  );
  const valueErrorField = valueError ? valueError?.fields?.find((nodeField) => nodeField.id === 'value') : null;
  const valueErrorMessage = valueErrorField ? valueErrorField.message : undefined;

  return (
    <Container>
      <Label title='Condition' tooltip={field.description} />
      <Expression
        key={`${command.node.id}-input`}
        value={input}
        title='Condition Input'
        dataEdges={dataEdges}
        description={field.description}
        setValue={(value: string) => updateField(value, 'input')}
        options={options}
        placeholder='Input'
        error={inputErrorMessage}
      />
      <Select
        key={`${command.node.id}-operator`}
        value={operator as string}
        setValue={(value) => updateField(value, 'operator')}
        options={searchTypeOptions}
        placeholder='Select an index search type'
      />
      {!searchOptionsWithoutValue.includes(operator) && (
        <Expression
          key={`${command.node.id}-value`}
          value={value}
          title='Condition Value'
          dataEdges={dataEdges}
          description={field.description}
          setValue={(value: string) => updateField(value, 'value')}
          options={options}
          placeholder='Value'
          error={valueErrorMessage}
        />
      )}
    </Container>
  );
};
